/**
 * Oriented bounding box (OBB) for a 2D point set, via the rotating-
 * calipers algorithm on the convex hull.
 *
 * Toussaint's theorem: the minimum-area enclosing rectangle of a
 * convex polygon has at least one side parallel to one of the
 * polygon's edges. So we only need to test as many orientations as
 * there are hull edges.
 *
 * Usage: pass an array of {x, z} points (e.g. floor polygon corners,
 * Voronoi-clipped section polygon). Returns the OBB or null if the
 * input has fewer than 3 distinct points.
 *
 * Pure: takes data in, returns the OBB. No external deps.
 */

/**
 * 2D cross product (z-up) of (a - o) and (b - o). Sign tells which
 * side of o→a the point b lies on. > 0 means CCW turn.
 */
function cross(o, a, b) {
  return (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
}

/**
 * Andrew's monotone-chain convex hull. Returns the hull in CCW order
 * (X-Z plane, math convention). O(n log n).
 */
function convexHull(points) {
  if (points.length < 3) return points.slice();
  const sorted = points.slice().sort((a, b) => a.x - b.x || a.z - b.z);

  const lower = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }

  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }

  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

/**
 * Project a list of points onto an orthonormal basis (u, n) anchored
 * at `origin`, return {minU, maxU, minN, maxN}.
 */
function projectExtents(points, origin, u, n) {
  let minU = Infinity;
  let maxU = -Infinity;
  let minN = Infinity;
  let maxN = -Infinity;
  for (const p of points) {
    const dx = p.x - origin.x;
    const dz = p.z - origin.z;
    const pu = dx * u.x + dz * u.z;
    const pn = dx * n.x + dz * n.z;
    if (pu < minU) minU = pu;
    if (pu > maxU) maxU = pu;
    if (pn < minN) minN = pn;
    if (pn > maxN) maxN = pn;
  }
  return { minU, maxU, minN, maxN };
}

/**
 * Compute the minimum-area OBB of a 2D point set in the X-Z plane.
 *
 * @param {Array<{x, z}>} points
 * @returns {{
 *   center: {x, z},
 *   axis1: {x, z},      // unit vector along the longer side
 *   axis2: {x, z},      // unit vector along the shorter side (perpendicular)
 *   length1: number,    // length along axis1, length1 >= length2
 *   length2: number,
 *   corners: Array<{x, z}>  // CCW: from center, ±halfL1*axis1 ± halfL2*axis2
 * } | null}
 */
function orientedBoundingBox(points) {
  const hull = convexHull(points);
  if (hull.length < 3) return null;

  let best = null;
  let bestArea = Infinity;

  for (let i = 0; i < hull.length; i++) {
    const a = hull[i];
    const b = hull[(i + 1) % hull.length];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len = Math.hypot(dx, dz);
    if (len < 1e-9) continue;

    const u = { x: dx / len, z: dz / len };
    const n = { x: -u.z, z: u.x };

    const { minU, maxU, minN, maxN } = projectExtents(hull, a, u, n);
    const area = (maxU - minU) * (maxN - minN);
    if (area < bestArea) {
      bestArea = area;
      best = { origin: a, u, n, minU, maxU, minN, maxN };
    }
  }

  const halfU = (best.maxU - best.minU) / 2;
  const halfN = (best.maxN - best.minN) / 2;
  const midU = (best.minU + best.maxU) / 2;
  const midN = (best.minN + best.maxN) / 2;
  const center = {
    x: best.origin.x + midU * best.u.x + midN * best.n.x,
    z: best.origin.z + midU * best.u.z + midN * best.n.z,
  };

  const sideU = best.maxU - best.minU;
  const sideN = best.maxN - best.minN;

  // Order so axis1 is the longer side.
  let axis1, axis2, length1, length2;
  if (sideU >= sideN) {
    axis1 = best.u;
    axis2 = best.n;
    length1 = sideU;
    length2 = sideN;
  } else {
    axis1 = best.n;
    axis2 = best.u;
    length1 = sideN;
    length2 = sideU;
  }

  const half1 = length1 / 2;
  const half2 = length2 / 2;
  const corners = [
    {
      x: center.x - half1 * axis1.x - half2 * axis2.x,
      z: center.z - half1 * axis1.z - half2 * axis2.z,
    },
    {
      x: center.x + half1 * axis1.x - half2 * axis2.x,
      z: center.z + half1 * axis1.z - half2 * axis2.z,
    },
    {
      x: center.x + half1 * axis1.x + half2 * axis2.x,
      z: center.z + half1 * axis1.z + half2 * axis2.z,
    },
    {
      x: center.x - half1 * axis1.x + half2 * axis2.x,
      z: center.z - half1 * axis1.z + half2 * axis2.z,
    },
  ];

  return { center, axis1, axis2, length1, length2, corners };
}

/**
 * Build an OBB whose axes are constrained to a given set of allowed
 * directions, instead of the convex-hull edges. Useful when the
 * polygon's hull contains edges that don't correspond to real walls
 * (e.g. Voronoi bisector edges between sections of an open-plan room)
 * — passing in the section's wall directions keeps the OBB aligned
 * with actual cabinet runs.
 *
 * @param {Array<{x, z}>} points
 * @param {Array<{x, z}>} directions - unit vectors. Each one is tested
 *   as a primary axis. Antiparallel duplicates are equivalent and only
 *   need to be passed once.
 * @returns Same shape as `orientedBoundingBox`, or null if either
 *   argument is empty.
 */
function wallAlignedBoundingBox(points, directions) {
  if (!points || points.length < 3) return null;
  if (!directions || directions.length === 0) return null;

  let best = null;
  let bestArea = Infinity;

  for (const dir of directions) {
    const u = { x: dir.x, z: dir.z };
    const n = { x: -u.z, z: u.x };
    const origin = points[0];

    const { minU, maxU, minN, maxN } = projectExtents(points, origin, u, n);
    const area = (maxU - minU) * (maxN - minN);
    if (area < bestArea) {
      bestArea = area;
      best = { origin, u, n, minU, maxU, minN, maxN };
    }
  }

  if (!best) return null;

  const halfU = (best.maxU - best.minU) / 2;
  const halfN = (best.maxN - best.minN) / 2;
  const midU = (best.minU + best.maxU) / 2;
  const midN = (best.minN + best.maxN) / 2;
  const center = {
    x: best.origin.x + midU * best.u.x + midN * best.n.x,
    z: best.origin.z + midU * best.u.z + midN * best.n.z,
  };

  const sideU = best.maxU - best.minU;
  const sideN = best.maxN - best.minN;

  let axis1, axis2, length1, length2;
  if (sideU >= sideN) {
    axis1 = best.u;
    axis2 = best.n;
    length1 = sideU;
    length2 = sideN;
  } else {
    axis1 = best.n;
    axis2 = best.u;
    length1 = sideN;
    length2 = sideU;
  }

  const half1 = length1 / 2;
  const half2 = length2 / 2;
  const corners = [
    {
      x: center.x - half1 * axis1.x - half2 * axis2.x,
      z: center.z - half1 * axis1.z - half2 * axis2.z,
    },
    {
      x: center.x + half1 * axis1.x - half2 * axis2.x,
      z: center.z + half1 * axis1.z - half2 * axis2.z,
    },
    {
      x: center.x + half1 * axis1.x + half2 * axis2.x,
      z: center.z + half1 * axis1.z + half2 * axis2.z,
    },
    {
      x: center.x - half1 * axis1.x + half2 * axis2.x,
      z: center.z - half1 * axis1.z + half2 * axis2.z,
    },
  ];

  return { center, axis1, axis2, length1, length2, corners };
}

/**
 * Deduplicate wall directions into unit vectors. Antiparallel walls
 * produce the same OBB axis, so we collapse them by always sign-
 * normalizing to the upper-half-plane (force x >= 0; if x == 0, force
 * z >= 0). Tolerance picks up rounding wobble in the input.
 */
function uniqueWallDirections(walls, tolerance = 1e-3) {
  const out = [];
  for (const w of walls || []) {
    const ry = w?.rotation?.y;
    if (ry == null) continue;
    let ux = Math.cos(ry);
    let uz = -Math.sin(ry);
    if (ux < -tolerance || (Math.abs(ux) <= tolerance && uz < 0)) {
      ux = -ux;
      uz = -uz;
    }
    const dup = out.some(
      (d) => Math.abs(d.x - ux) < tolerance && Math.abs(d.z - uz) < tolerance
    );
    if (!dup) out.push({ x: ux, z: uz });
  }
  return out;
}

module.exports = {
  orientedBoundingBox,
  wallAlignedBoundingBox,
  uniqueWallDirections,
  convexHull,
};
